import { Component, OnInit, OnDestroy } from "@angular/core";

import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import {
  ActivatedRoute,
  Router,
  NavigationEnd,
  NavigationStart,
} from "@angular/router";

import { IProduct } from "src/app/commons/product-card/IProduct";
import { ProductService } from "src/app/commons/services/product.service";
import {
  CartService,
  AnalyticsService,
  SeoService,
  SiteConfigService,
} from "src/app/core/services/";
import { DisplayGroupService, GeneralService } from "src/app/commons/services";
import { CollicoService } from "src/app/commons/services/collico.service";

@Component({
  selector: "sv-detail",
  templateUrl: "./detail.component.html",
  styleUrls: ["./detail.component.scss"],
  providers: [ProductService],
})
export class DetailComponent implements OnInit, OnDestroy {
  // defaults
  private unsubscribe$ = new Subject<void>();
  images = [944, 1011, 984].map((n) => `https://picsum.photos/id/${n}/900/500`);
  data: IProduct;
  id: string = "";
  basePrice: string = "";
  subPrice: string = "";
  ingredientsBox: boolean = false;
  jsonLdSchema = {};
  loading: boolean = false;
  itemLoaded: boolean = false;
  legend: string = "";
  productDisplayGroup: any = null;

  constructor(
    private cartService: CartService,
    private route: ActivatedRoute,
    private analyticsService: AnalyticsService,
    private productService: ProductService,
    private seoService: SeoService,
    private router: Router,
    public generalService: GeneralService,
    public siteConfigService: SiteConfigService,
    public displayGroupService: DisplayGroupService,
    public collicoService: CollicoService
  ) {
    this.id = this.handleGetProductId();
    this.analyticsService.currentProductId = parseInt(this.id);
  }

  async ngOnInit() {
    // observe product detail loading
    this.productService
      .observeDetailLoading()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((loading) => {
        this.loading = loading;
      });

    // observe router change
    this.router.events
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(async (evt) => {
        if (!(evt instanceof NavigationEnd)) {
          return;
        }
        this.itemLoadedChange(false);

        const id = this.handleGetProductId();
        this.data = await this.productService.getProduct(id);

        if (this.data && this.data.C001) {
          this.makeProductDetailsMeta();
        }

        this.basePrice = "";
        this.subPrice = "";
        this.populateProductData();
      });

    // Subscribe to date change
    this.collicoService.collicoDeliveryDateAndTimeChanged$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(async (dateObj) => {
        if (dateObj) {
          this.itemLoadedChange(false);

          const id = this.handleGetProductId();
          this.data = await this.productService.getProduct(id);
        }
      });

    this.itemLoadedChange(false);
    this.data = await this.productService.getProduct(this.id);

    if (this.data && this.data.C001) {
      this.makeProductDetailsMeta();
    }

    this.populateProductData();
    const { C000, F004 } = this.data;

    // this.analyticsService.productViewEvent(C000, F004, this.data);

    // product not found, navigate to root
    // if (!this.data.C000) {
    //   this.router.navigate(["/"]);
    // }
    // this.makeLegend();
  }

  itemLoadedChange(value: boolean): void {
    this.itemLoaded = value;
  }

  focusOnProduct(): void {
    const document = this.generalService.getDocument;
    const productCard = document.querySelector(
      ".c-productCard:first-of-type"
    ) as HTMLElement;
    if (productCard) {
      productCard.focus();
      this.itemLoadedChange(true);
    } else {
      this.itemLoadedChange(false);
    }
  }

  populateProductData() {
    const { F000, F001, D001, C002, C000, F012, F004, F018 } = this.data;

    // parse images
    if (F000 && !Array.isArray(F000)) {
      this.images = [F000];
    }
    // parse price
    if (!!this.data && F004) {
      let prices: string | string[] = F018 + "";
      prices = prices.split(".");

      this.basePrice = prices[0];
      this.subPrice = prices[1];
      if (this.subPrice && this.subPrice.length === 1) {
        this.subPrice += "0";
      }
    }
    try {
      const canonicalUrl = this.seoService.generateCanonicalUrl();
      const desc = this.data.L008 ?? "none provided";
      this.jsonLdSchema = {
        "@context": "http://schema.org",
        "@type": "Product",
        name: F001,
        category: D001,
        brand: {
          "@type": "Brand",
          name: this.data.F101,
        },
        image: F000,
        productID: C000,
        url: canonicalUrl,
        description: desc,
        offers: {
          "@type": "Offer",
          url: canonicalUrl,
          itemCondition: "https://schema.org/NewCondition",
          availability: "https://schema.org/InStock",
          price: this.getPrice(),
          priceCurrency: "EUR",
        },
      };
      this.seoService.updateMetaDescription(desc);
    } catch (e) {
      console.error("error in setting offers: ", e);
    }
  }

  private getPrice() {
    return this.basePrice;
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  handleGetProductId() {
    return this.route.snapshot.paramMap.get("id");
  }

  toggleIngredients() {
    this.ingredientsBox = !this.ingredientsBox;
  }

  async makeProductDetailsMeta() {
    const { C000, F000, F001, F002, F005, D001, F004 } = this.data;

    let pageTitle = F001 + " " + F002 + " " + F005;
    this.seoService.setTitle = pageTitle;
    this.seoService.setFaceBookMeta(pageTitle, F000);

    let document = this.generalService.getDocument;
    let { href } = document.location;
    this.analyticsService.screenView(href, pageTitle);
    this.analyticsService.productViewEvent(C000, F004, this.data);

    const siteName = this.siteConfigService.getTenantTitle;
    let currentGroup = await this.findCurrentDisplayGroupData(D001);

    if (currentGroup && currentGroup.name) {
      let title =
        (F001 || "") +
        " " +
        (F002 || "") +
        " " +
        (F005 || "") +
        " - " +
        (currentGroup.name || "") +
        " | " +
        (siteName || "");
      this.seoService.setTitle = title;
      this.seoService.setFaceBookMeta(title, F000);
    }
  }

  async findCurrentDisplayGroupData(D001) {
    this.productDisplayGroup = null;
    const dGroups: any = this.displayGroupService.displayGroups;

    for (let index = 0; index < dGroups.length; index++) {
      const parentGroup = dGroups[index];

      if (this.data.D001.length === 3 && parentGroup.code === D001) {
        this.productDisplayGroup = parentGroup;
      }

      if (this.data.D001.length === 6) {
        let childGroups = parentGroup.display_groups;

        for (let j = 0; j < childGroups.length; j++) {
          const childGroup = childGroups[j];
          if (childGroup.code === D001) {
            this.productDisplayGroup = childGroup;
          }
        }
      }
    }

    if (this.productDisplayGroup) {
      return this.productDisplayGroup;
    }
  }

  // makeLegend() {
  //   let { L007, L008, L009 } = this.data;
  //   let nutritionalValue = "";
  //   let disclaimerText = "";

  //   // F100 = L008 (Lead text) + L009 (Body text) + L007 (Ingredient list) + Nutritional values + Disclaimer text

  //   this.legend = L009;
  // }
}
